import { UnifiedAlarm } from '@hakimo-ui/hakimo/types';
import { CameraAlarmTracker } from '../types';

export const areSetsEqual = (a: Set<string>, b: Set<string>) =>
  a.size === b.size && [...a].every((value) => b.has(value));

/**
 * Cameras that play frames. A camera with any frames alarm plays them as one
 * frames track and never mounts a video player for its video alarms (the
 * `hasFrames` check in CameraMonitorView), so those can never report canplay;
 * AlarmPreloader leaves them out of the buffer gate. The two agree because
 * both are fed the same alarm list (updatedAlarms + updatedNeighbourAlarms).
 */
export function getFramesCameraIds(alarms: UnifiedAlarm[]): Set<string> {
  return new Set(
    alarms
      .filter((alarm) => alarm.media?.type === 'frames')
      .map((alarm) => alarm.cameraId)
  );
}

/**
 * Given an array of intervals [start, end],
 * returns an array of gaps where no interval is active.
 *
 * @param {Array<[number, number]>} intervals
 * @returns {Array<[number, number]>}
 */
export function findGaps(
  intervals: Array<[number, number]>
): Array<[number, number]> {
  if (!intervals.length) return [];

  intervals.sort((a, b) => a[0] - b[0]);

  const gaps: Array<[number, number]> = [];
  let currentEnd = intervals[0][1];

  for (let i = 1; i < intervals.length; i++) {
    const [start, end] = intervals[i];

    // Found a gap
    if (start > currentEnd) {
      gaps.push([currentEnd, start]);
    }

    // Extend coverage if needed
    currentEnd = Math.max(currentEnd, end);
  }

  return gaps;
}

export const getTimelineGaps = (
  alarms: UnifiedAlarm[],
  timelineStartTime: number,
  timelineEndTime: number
) => {
  // Array of tuple of alarmStartTime and alarmEndTime
  const alarmTimestampTuple: Array<[number, number]> = alarms.map((alarm) => {
    return [alarm.alarmStartTime, alarm.alarmEndTime];
  });

  const timelineGaps = findGaps(alarmTimestampTuple);

  const minAlarmsStartTime = Math.min(
    ...alarms.map((alarm) => alarm.alarmStartTime)
  );
  const maxAlarmsEndTime = Math.max(
    ...alarms.map((alarm) => alarm.alarmEndTime)
  );
  if (minAlarmsStartTime > timelineStartTime) {
    timelineGaps.unshift([timelineStartTime, minAlarmsStartTime]);
  }
  if (maxAlarmsEndTime < timelineEndTime) {
    timelineGaps.push([maxAlarmsEndTime, timelineEndTime]);
  }

  return timelineGaps;
};

export const getDefaultCameraAlarmTracker = (): CameraAlarmTracker => {
  return {
    currentAlarmId: '',
    preloadAlarmIds: new Set(),
    showAlarmIds: new Set(),
  };
};

function areCameraAlarmTrackersEqual(
  a: CameraAlarmTracker,
  b: CameraAlarmTracker
): boolean {
  return (
    a.currentAlarmId === b.currentAlarmId &&
    a.isCamShownCurrently === b.isCamShownCurrently &&
    areSetsEqual(a.showAlarmIds, b.showAlarmIds) &&
    areSetsEqual(a.preloadAlarmIds, b.preloadAlarmIds)
  );
}

export function areCameraAlarmTrackerMapsEqual(
  mapA: Map<string, CameraAlarmTracker>,
  mapB: Map<string, CameraAlarmTracker>
): boolean {
  // Quick size check first (O(1))
  if (mapA.size !== mapB.size) {
    return false;
  }

  // Check each key-value pair
  for (const [key, valueA] of mapA) {
    const valueB = mapB.get(key);
    if (!valueB || !areCameraAlarmTrackersEqual(valueA, valueB)) {
      return false;
    }
  }

  return true;
}
